import React, { useState, useEffect, useCallback } from 'react';
import { formatUpdatedAgo } from '../utils/formatUtils';

const STALE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes
const TICK_INTERVAL_MS = 10 * 1000; // 10 seconds

/**
 * Shared freshness indicator for analytics surfaces.
 *
 * Shows how fresh the displayed data is with auto-ticking relative time,
 * a colour-coded dot (green = fresh, amber = stale), and an optional
 * refresh button.
 *
 * @param {number}   timestamp    - Unix ms of last successful data fetch
 * @param {Function} [onRefresh]  - If provided, renders a refresh button
 * @param {string}   [className]  - Extra classes for the wrapper
 */
export default function FreshnessIndicator({ timestamp, onRefresh, className = '' }) {
  const [, setTick] = useState(0);

  // Re-render every 10s so the relative label stays accurate
  useEffect(() => {
    if (!timestamp) return;
    const id = setInterval(() => setTick(t => t + 1), TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [timestamp]);

  const handleRefresh = useCallback(() => {
    if (onRefresh) onRefresh();
  }, [onRefresh]);

  if (!timestamp) return null;

  const isStale = Date.now() - timestamp >= STALE_THRESHOLD_MS;
  const label = formatUpdatedAgo(timestamp);

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${className}`}>
      {/* Status dot */}
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          isStale
            ? 'bg-amber-400'
            : 'bg-green-400 animate-pulse'
        }`}
      />
      {/* Relative time */}
      <span className={isStale ? 'text-amber-400/80' : 'gch-text-muted'}>
        Updated {label}
      </span>
      {/* Refresh button */}
      {onRefresh && (
        <button
          type="button"
          onClick={handleRefresh}
          className="ml-0.5 p-0.5 rounded hover:bg-white/10 transition-colors gch-text-muted hover:gch-text-secondary"
          title="Stats sync every few minutes. Tap refresh for the latest."
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M20.992 4.382v4.992" />
          </svg>
        </button>
      )}
    </span>
  );
}
